import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Copy, CheckCircle2, ExternalLink, RefreshCw, ArrowLeft } from "lucide-react";
import { api, formatBRL, formatApiErrorDetail } from "../../lib/api";
import { CountdownTimer } from "../../components/booking/CountdownTimer";
import { loadPending, clearPending, clearQuote, loadQuote } from "../../lib/booking-storage";
import { useSEO } from "../../lib/seo";
import { track } from "../../lib/tracking";
import { toast } from "sonner";

export default function ReservarPagamento() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const rid = params.get("rid") || loadPending()?.reservation_id;
  const token = params.get("token") || loadPending()?.public_token;

  const [info, setInfo] = useState(null);     // /reservations/:id (detail)
  const [status, setStatus] = useState(null); // /reservations/:id/status
  const [invoice, setInvoice] = useState(null); // pulled separately from quote saved data is not enough; we will fetch via status which already returns invoice
  const [copied, setCopied] = useState(false);
  const [expired, setExpired] = useState(false);
  const pollRef = useRef(null);

  const saved = loadQuote();

  useSEO({ title: "Pagamento", description: "Confirme sua reserva." });

  // Bootstrap
  useEffect(() => {
    if (!rid || !token) {
      nav("/espacos", { replace: true });
      return;
    }
    let mounted = true;
    Promise.all([
      api.get(`/public/reservations/${rid}`, { params: { token } }),
      api.get(`/public/reservations/${rid}/status`, { params: { token } }),
    ])
      .then(([detail, st]) => {
        if (!mounted) return;
        setInfo(detail.data);
        setStatus(st.data);
        // Confirmed already?
        if (st.data?.status === "confirmed") {
          clearPending(); clearQuote();
          track("payment_succeeded", { reservation_id: rid });
          nav(`/reserva/${rid}/confirmada?token=${token}`, { replace: true });
        } else if (st.data?.status === "expired") {
          setExpired(true);
        }
      })
      .catch((e) => {
        toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Reserva não encontrada");
        nav("/espacos", { replace: true });
      });
    return () => { mounted = false; };
  }, [rid, token, nav]);

  // We need the PIX/payment fields. They come from the create response — re-use loadQuote? Actually
  // they're only in the original create response; let's pull from the invoices via a soft endpoint?
  // The status endpoint returns invoice {status, asaas_status, paid_at}. The QR/link were returned by
  // the create call. We persisted nothing of those — let's fix by storing them at savePending time.
  // For now, the create-response screen will redirect here with rid+token; the actual payment
  // assets (qr/link) we expose via /reservations/:id (the detail) -> let's also extend backend later.
  // Workaround: detail endpoint already has reservation+space. We need invoice with QR. Let me
  // gracefully fetch it from /invoices/:id (admin-only) — not feasible publicly.
  // Decision: store qr/link in localStorage at create time via savePending. We'll read it here.
  const pending = loadPending();

  // Polling — 4s while pending_payment
  const startPolling = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/public/reservations/${rid}/status`, { params: { token } });
        setStatus(data);
        if (data?.status === "confirmed") {
          clearInterval(pollRef.current);
          clearPending(); clearQuote();
          track("payment_succeeded", { reservation_id: rid });
          nav(`/reserva/${rid}/confirmada?token=${token}`, { replace: true });
        }
        if (data?.status === "expired") {
          clearInterval(pollRef.current);
          setExpired(true);
        }
      } catch { /* keep polling */ }
    }, 4000);
  }, [rid, token, nav]);

  useEffect(() => {
    if (!status) return undefined;
    if (status.status === "pending_payment" && !expired) startPolling();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [status, expired, startPolling]);

  const handleExpire = () => {
    setExpired(true);
    if (pollRef.current) clearInterval(pollRef.current);
  };

  const copyPix = () => {
    const code = pending?.pix_copy_paste;
    if (!code) { toast.error("Código PIX indisponível"); return; }
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      toast.success("Código PIX copiado!");
      setTimeout(() => setCopied(false), 2500);
    });
  };

  if (!info || !status) {
    return <div className="min-h-screen flex items-center justify-center text-gray-400">Carregando reserva...</div>;
  }

  if (expired) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6" data-testid="expired-screen">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
          <div className="text-6xl mb-4">⏱️</div>
          <h1 className="font-display text-2xl text-[#0A1128] mb-2">Reserva expirada</h1>
          <p className="text-gray-600 mb-6">
            O prazo de 30 minutos para pagamento foi atingido. O horário foi liberado para outros clientes.
          </p>
          <button
            onClick={() => nav(`/espacos/${info.space.id}`)}
            className="w-full bg-[#0A1128] text-white rounded-xl py-3 font-bold uppercase tracking-wider text-sm hover:bg-[#14213D]"
            data-testid="retry-booking"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  const reservation = info.reservation;
  const space = info.space;
  const startD = new Date(reservation.start_time);
  const endD = new Date(reservation.end_time);
  const pixCode = pending?.pix_copy_paste;
  const paymentLink = pending?.payment_link;
  const method = pending?.payment_method || "pix";

  // QR Code rendered via api.qrserver.com (no extra deps)
  const qrUrl = pixCode
    ? `https://api.qrserver.com/v1/create-qr-code/?size=320x320&margin=8&data=${encodeURIComponent(pixCode)}`
    : null;

  return (
    <div className="bg-gray-50 min-h-screen pb-12" data-testid="pagamento-page">
      <div className="sticky top-0 z-30 bg-white/80 backdrop-blur border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to={`/espacos/${space.id}`} className="flex items-center gap-2 text-sm font-semibold text-[#0A1128]" data-testid="back-link">
            <ArrowLeft size={18} /> Cancelar
          </Link>
          <div className="text-xs uppercase tracking-widest text-gray-500">
            Etapa <span className="text-[#D4AF37] font-bold">3</span> de 3
          </div>
        </div>
        <div className="h-1 bg-gray-200">
          <div className="h-full bg-[#D4AF37]" style={{ width: "100%" }} />
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 lg:py-8 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        <div className="space-y-5">
          <CountdownTimer
            expiresAt={reservation.payment_status === "paid" ? null : status.pending_expires_at}
            onExpire={handleExpire}
          />

          {method === "pix" && (
            <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-5 sm:p-6 text-center" data-testid="pix-block">
              <div className="text-xs uppercase tracking-widest text-gray-500 font-bold mb-3">
                Pague com PIX
              </div>
              {qrUrl ? (
                <img src={qrUrl} alt="QR Code PIX" className="mx-auto rounded-xl" style={{ maxWidth: 280 }} data-testid="pix-qr-image" />
              ) : (
                <div className="aspect-square bg-gray-100 rounded-xl flex items-center justify-center text-gray-400">
                  QR indisponível
                </div>
              )}
              <div className="text-gray-700 text-sm mt-4">
                Aponte a câmera do seu app do banco
              </div>
              <button
                type="button"
                onClick={copyPix}
                disabled={!pixCode}
                data-testid="copy-pix-button"
                className="w-full mt-4 rounded-xl border-2 border-[#0A1128] py-3 font-bold text-sm uppercase tracking-wider text-[#0A1128] hover:bg-[#0A1128] hover:text-white active:scale-[0.99] transition-all flex items-center justify-center gap-2"
              >
                {copied ? <><CheckCircle2 size={16} /> Copiado!</> : <><Copy size={16} /> Copiar código PIX</>}
              </button>
              {paymentLink && (
                <a
                  href={paymentLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="payment-link"
                  className="block mt-3 text-sm text-gray-500 hover:text-[#D4AF37] underline inline-flex items-center gap-1 mx-auto w-fit"
                >
                  Abrir comprovante <ExternalLink size={13} />
                </a>
              )}
            </div>
          )}

          {(method === "cartao" || method === "boleto") && paymentLink && (
            <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-6 text-center" data-testid="external-payment-block">
              <div className="text-xs uppercase tracking-widest text-gray-500 font-bold mb-2">
                {method === "cartao" ? "Cartão de crédito" : "Boleto bancário"}
              </div>
              <p className="text-gray-700 mb-4">
                {method === "cartao"
                  ? "Você será redirecionado para a tela segura do Mercado Pago para finalizar com seu cartão."
                  : "Gere e pague seu boleto. A compensação leva até 1 dia útil."}
              </p>
              <a
                href={paymentLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#0A1128] text-white rounded-xl px-6 py-3 font-bold uppercase tracking-wider text-sm hover:bg-[#14213D]"
              >
                Abrir pagamento <ExternalLink size={16} />
              </a>
            </div>
          )}

          <div className="flex items-center gap-2 text-xs text-gray-500 justify-center" data-testid="polling-indicator">
            <RefreshCw size={12} className="animate-spin" /> Aguardando confirmação automática...
          </div>
        </div>

        {/* Summary aside */}
        <aside className="lg:sticky lg:top-20 lg:self-start">
          <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
            {space.photos?.[0] && (
              <img
                src={space.photos[0].startsWith("http") ? space.photos[0] : `${process.env.REACT_APP_BACKEND_URL}${space.photos[0]}`}
                alt={space.name}
                className="w-full aspect-[16/9] object-cover"
              />
            )}
            <div className="p-4 space-y-2 text-sm">
              <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-bold">{space.category}</div>
              <div className="font-bold text-[#0A1128]">{space.name}</div>
              <div className="text-gray-600">
                {startD.toLocaleDateString("pt-BR", { day: "2-digit", month: "long" })}
                {" · "}
                {startD.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                {" às "}
                {endD.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
              </div>
              <div className="text-gray-600">👤 {reservation.guest_name}</div>
              <div className="border-t border-gray-200 pt-3 mt-3 flex items-baseline justify-between">
                <span className="font-semibold text-gray-600">Total</span>
                <span className="text-2xl font-black text-[#0A1128]">{formatBRL(reservation.amount)}</span>
              </div>
              <div className="text-[11px] uppercase tracking-widest font-bold text-amber-700 bg-amber-50 rounded-md px-2 py-1 mt-2 inline-block">
                Aguardando pagamento
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
